import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import {
  exportCsv,
  formatDuration,
  formatTotalHours,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';
import type { ReportTrip } from '@/api/types';

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

function formatDay(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
}

/** YYYY-MM-DD em hora local — chave de agrupamento dos trips por dia. */
function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface ColorBucket {
  /** classes do chip colorido aplicado ao total de horas do dia */
  chipBg: string;
  chipText: string;
  label: string;
}

/**
 * Cor do chip aplicada ao total de horas trabalhadas do dia.
 * - 0: neutro (não trabalhou)
 * - até 1h: amarelo
 * - até 2h: laranja
 * - até 5h: roxo
 * - acima: vermelho
 */
function colorForHours(ms: number): ColorBucket {
  if (!ms || ms <= 0)
    return { chipBg: 'bg-muted', chipText: 'text-muted-foreground', label: 'sem trabalho' };
  if (ms <= 1 * HOUR_MS)
    return { chipBg: 'bg-yellow-500', chipText: 'text-yellow-950', label: 'até 1h' };
  if (ms <= 2 * HOUR_MS)
    return { chipBg: 'bg-orange-500', chipText: 'text-white', label: '1h a 2h' };
  if (ms <= 5 * HOUR_MS)
    return { chipBg: 'bg-purple-500', chipText: 'text-white', label: '2h a 5h' };
  return { chipBg: 'bg-red-500', chipText: 'text-white', label: 'acima de 5h' };
}

/** Marcas da régua de 24h: 9 pontos, espaçamento de 3h. */
const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

/**
 * Gráfico SVG cobrindo 0h-24h. Cada trip do dia vira um retângulo
 * posicionado proporcionalmente à hora local. Régua a cada 3h com
 * label numerado abaixo. SVG evita qualquer interferência de table
 * layout, flexbox ou CSS herdado.
 */
function DayBar({
  trips,
  dayKey,
}: {
  trips: ReportTrip[];
  dayKey: string;
}) {
  const dayStart = new Date(`${dayKey}T00:00:00`).getTime();

  // Coordenadas internas do viewBox. O SVG se redimensiona pra largura
  // do contêiner, então o viewBox define só a proporção.
  const VB_W = 480;
  const BAR_TOP = 0;
  const BAR_H = 20;
  const BAR_BOTTOM = BAR_TOP + BAR_H;
  const LABEL_Y = BAR_BOTTOM + 14;
  const VB_H = LABEL_Y + 4;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: VB_H, display: 'block' }}
    >
      {/* fundo da barra */}
      <rect
        x={0}
        y={BAR_TOP}
        width={VB_W}
        height={BAR_H}
        rx={3}
        ry={3}
        fill="hsl(var(--muted))"
        opacity={0.4}
        stroke="hsl(var(--border))"
        strokeWidth={1}
      />
      {/* blocos de trabalho */}
      {trips.map((t, i) => {
        const s = new Date(t.startTime).getTime();
        const e = new Date(t.endTime).getTime();
        const left = Math.max(0, (s - dayStart) / DAY_MS);
        const right = Math.min(1, (e - dayStart) / DAY_MS);
        const width = Math.max(0.002, right - left);
        return (
          <rect
            key={i}
            x={left * VB_W}
            y={BAR_TOP + 1}
            width={width * VB_W}
            height={BAR_H - 2}
            fill="hsl(var(--primary))"
          >
            <title>
              {new Date(t.startTime).toLocaleTimeString('pt-BR')} →{' '}
              {new Date(t.endTime).toLocaleTimeString('pt-BR')}
            </title>
          </rect>
        );
      })}
      {/* ticks + labels */}
      {TICK_HOURS.map((h) => {
        const x = (h / 24) * VB_W;
        return (
          <g key={h}>
            <line
              x1={x}
              x2={x}
              y1={BAR_TOP}
              y2={BAR_BOTTOM}
              stroke="hsl(var(--border))"
              strokeWidth={1}
            />
            <text
              x={x}
              y={LABEL_Y}
              fontSize={11}
              fontFamily="monospace"
              fill="hsl(var(--muted-foreground))"
              textAnchor={h === 0 ? 'start' : h === 24 ? 'end' : 'middle'}
            >
              {String(h).padStart(2, '0')}h
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function HoursReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const {
    deviceId,
    setDeviceId,
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    customError,
    executed,
    generate,
  } = useReportFilters('multi');

  // Período acima de 1 dia: pede ao Traccar uma linha por dia/máquina (daily=true).
  // Caso contrário, agrega tudo num resumo por máquina.
  const rangeMs = executed
    ? new Date(executed.range.to).getTime() - new Date(executed.range.from).getTime()
    : 0;
  const isDaily = rangeMs > DAY_MS;

  const query = useQuery({
    queryKey: ['report', 'hours', executed, isDaily],
    queryFn: () =>
      reportsApi.summary({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
        daily: isDaily,
      }),
    enabled: executed !== null,
  });

  // Em modo daily, pegamos também os trips do mesmo período pra montar a
  // janela horária na tabela. Em modo agregado (1 dia), não precisa.
  const tripsQuery = useQuery({
    queryKey: ['report', 'hours-trips', executed],
    queryFn: () =>
      reportsApi.trips({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null && isDaily,
  });

  const tripsByKey = useMemo(() => {
    const map = new Map<string, ReportTrip[]>();
    for (const t of tripsQuery.data ?? []) {
      const key = `${t.deviceId}|${localDayKey(t.startTime)}`;
      const list = map.get(key);
      if (list) list.push(t);
      else map.set(key, [t]);
    }
    return map;
  }, [tripsQuery.data]);

  const rows = useMemo(() => {
    const list = query.data ?? [];
    if (isDaily) {
      return [...list].sort((a, b) => {
        const da = a.startTime ?? '';
        const db = b.startTime ?? '';
        if (da !== db) return db.localeCompare(da); // mais recente primeiro
        return (a.deviceName ?? '').localeCompare(b.deviceName ?? '', 'pt-BR');
      });
    }
    return [...list].sort((a, b) => (b.engineHours ?? 0) - (a.engineHours ?? 0));
  }, [query.data, isDaily]);

  const total = rows.reduce((sum, r) => sum + (r.engineHours ?? 0), 0);

  const handleExport = () => {
    if (!executed) return;
    const header = isDaily
      ? ['Data', 'Máquina', 'Horas trabalhadas', 'Horas (ms)', 'Faixa']
      : ['Máquina', 'Horas trabalhadas', 'Horas (ms)'];
    const body = rows.map((r) => {
      const machine = r.deviceName ?? devices.get(r.deviceId)?.name ?? '';
      const duration = r.engineHours != null ? formatDuration(r.engineHours) : '';
      const ms = r.engineHours ?? '';
      if (isDaily) {
        const bucket = colorForHours(r.engineHours ?? 0).label;
        return [formatDay(r.startTime), machine, duration, ms, bucket];
      }
      return [machine, duration, ms];
    });
    exportCsv(
      `horas_trabalhadas_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      header,
      body,
    );
  };

  return (
    <ReportShell
      title="Horas de Trabalho"
      description="Tempo total em operação por máquina no período. Em períodos maiores que um dia, mostra o detalhamento por dia e a janela horária."
    >
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        customError={customError}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && !query.isLoading && rows.length > 0 && (
        <div className="space-y-0.5 text-xs text-muted-foreground">
          <p>
            Total de Horas Trabalhadas no período: <span className="font-semibold text-foreground">{formatDuration(total)}</span>
          </p>
          <p>
            Total de Horas Trabalhadas no período: <span className="font-semibold text-foreground">{formatTotalHours(total)}</span>
          </p>
        </div>
      )}
      {isDaily && executed && !query.isLoading && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span className="font-semibold">Legenda:</span>
          <LegendChip color="bg-muted" label="sem trabalho" />
          <LegendChip color="bg-yellow-500" label="até 1h" />
          <LegendChip color="bg-orange-500" label="1h – 2h" />
          <LegendChip color="bg-purple-500" label="2h – 5h" />
          <LegendChip color="bg-red-500" label="acima de 5h" />
        </div>
      )}
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={rows.length}
          unit={isDaily ? 'linhas (máquina · dia)' : 'máquinas'}
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={rows.length === 0}
      >
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              {isDaily && <th className="px-4 py-2 font-semibold">Data</th>}
              <th className="px-4 py-2 font-semibold">Máquina</th>
              <th className="px-4 py-2 font-semibold">Horas trabalhadas</th>
              {isDaily && <th className="px-4 py-2 font-semibold">Janela 24h</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const dayKey = r.startTime ? localDayKey(r.startTime) : '';
              const tripsOfDay = isDaily ? (tripsByKey.get(`${r.deviceId}|${dayKey}`) ?? []) : [];
              const color = colorForHours(r.engineHours ?? 0);
              return (
                <tr
                  key={`${r.deviceId}-${r.startTime ?? i}`}
                  className="border-b border-border last:border-0 hover:bg-accent/40"
                >
                  {isDaily && (
                    <td className="px-4 py-2 font-mono text-xs">{formatDay(r.startTime)}</td>
                  )}
                  <td className="px-4 py-2 font-semibold">{r.deviceName}</td>
                  <td className="px-4 py-2">
                    {r.engineHours != null ? (
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 font-mono text-xs font-bold',
                          color.chipBg,
                          color.chipText,
                        )}
                      >
                        {formatDuration(r.engineHours)}
                      </span>
                    ) : (
                      <span className="font-mono text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                  {isDaily && (
                    <td className="px-4 py-2" style={{ width: 360, minWidth: 360 }}>
                      <DayBar trips={tripsOfDay} dayKey={dayKey} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ReportTable>
    </ReportShell>
  );
}

function LegendChip({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('inline-block h-2.5 w-4 rounded-sm', color)} />
      {label}
    </span>
  );
}
